import React from "react";
import PropTypes from "prop-types";

import { Fab, Grid, InputBase, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import useIcon from "../hooks/useIcon";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1),
    minHeight: "80px",
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(2),
    borderColor: theme.palette.grey[300],
    borderWidth: "1px",
    borderStyle: "solid",
  },
  container_input: {
    padding: theme.spacing(1),
    minHeight: "68px",
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(2),
    borderColor: theme.palette.grey[300],
    borderWidth: "1px",
    borderStyle: "solid",
    width: "100%",
  },
  container_blank: {
    padding: theme.spacing(1),
    minHeight: "80px",
    borderRadius: theme.spacing(2),
  },
  grid: {
    display: "flex",
    flexFlow: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  fab: {
    zIndex: "0",
    boxShadow: "none",
    minWidth: "max-content !important",
    borderRadius: "14px !important",
  },
  fab_max: {
    minWidth: "initial",
    height: "initial",
    fontSize: "10px",
    padding: "0px 4px",
    marginLeft: theme.spacing(0.5),
    boxShadow: "none",
    borderRadius: theme.spacing(1),
  },
  coinImage: {
    width: "20px",
  },
  coinImageNegative: {
    width: "20px",
    marginLeft: `-${theme.spacing(1)}px`,
  },
  coinSymbol: {
    marginLeft: theme.spacing(0.5),
  },
  input: {
    ...theme.typography.h5,
    width: "100%",
  },
}));

CoinField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  activeField: PropTypes.bool.isRequired,
};

export function RemoveLiquidityField1({
  onClick1,
  onClick2,
  symbol1,
  symbol2,
  value,
  onChange,
  activeField1,
  liquidity,
}) {
  // This component is used to selecting a coin and entering a value, the props are explained below:
  //      onClick - (string) => void - Called when the button is clicked
  //      symbol - string - The text displayed on the button
  //      value - string - The value of the text field
  //      onChange - (e) => void - Called when the text field changes
  //      activeField - boolean - Whether text can be entered into this field or not
  const [iconImage] = useIcon();

  const classes = useStyles();

  return (
    <div className={classes.container_blank}>
      <Grid
        container
        direction="column"
        justifyContent="space-between"
        alignItems="center"
        className={`flex-wrap ${classes.grid}`}
      >
        {/* Button */}
        <Grid item xs={6} sm={3}>
          <Fab
            size="medium"
            variant="extended"
            color={symbol1 ? "default" : "primary"}
            onClick={onClick1}
            className={classes.fab}
          >
            {symbol1 ? (
              <>
                {typeof symbol1 === "string" ? (
                  <>
                    <img
                      src={iconImage(symbol1)}
                      alt={symbol1}
                      className={classes.coinImage}
                    />
                    <span className={classes.coinSymbol}>{symbol1}</span>
                  </>
                ) : (
                  <>
                    <img
                      src={iconImage(symbol1[0])}
                      alt={symbol1[0]}
                      className={classes.coinImage}
                    />
                    <img
                      src={iconImage(symbol1[1])}
                      alt={symbol1[1]}
                      className={classes.coinImageNegative}
                    />
                    <span className={classes.coinSymbol}>
                      {symbol1[0]}-{symbol1[1]}
                    </span>
                  </>
                )}
              </>
            ) : (
              "Select"
            )}
            <ExpandMoreIcon />
          </Fab>
        </Grid>

        {/* Button */}
        <Grid item xs={6} sm={3}>
          <Fab
            size="medium"
            variant="extended"
            color={symbol2 ? "default" : "primary"}
            onClick={onClick2}
            className={classes.fab}
          >
            {symbol2 ? (
              <>
                {typeof symbol2 === "string" ? (
                  <>
                    <img
                      src={iconImage(symbol2)}
                      alt={symbol2}
                      className={classes.coinImage}
                    />
                    <span className={classes.coinSymbol}>{symbol2}</span>
                  </>
                ) : (
                  <>
                    <img
                      src={iconImage(symbol2[0])}
                      alt={symbol2[0]}
                      className={classes.coinImage}
                    />
                    <img
                      src={iconImage(symbol2[1])}
                      alt={symbol2[1]}
                      className={classes.coinImageNegative}
                    />
                    <span className={classes.coinSymbol}>
                      {symbol2[0]}-{symbol2[1]}
                    </span>
                  </>
                )}
              </>
            ) : (
              "Select"
            )}
            <ExpandMoreIcon />
          </Fab>
        </Grid>

        {/* Text Field */}
        <Grid item xs={12} sm={6} className="mt-2 mt-sm-0">
          <InputBase
            value={value}
            onChange={onChange}
            placeholder="0.0"
            disabled={!activeField1}
            classes={{
              root: classes.container_input,
            }}
          />
        </Grid>
      </Grid>
      {!!liquidity && (
        <div className="d-flex align-items-center justify-content-end mt-2">
          <span>
            Your Liquidity:{" "}
            {Number(liquidity)
              .toFixed(18)
              .replace(/\.?0+$/, "")}
          </span>
          <Fab
            variant="extended"
            color="primary"
            className={classes.fab_max}
            onClick={() =>
              onChange({
                target: {
                  value: Number(liquidity)
                    .toFixed(18)
                    .replace(/\.?0+$/, ""),
                },
              })
            }
          >
            Max
          </Fab>
        </div>
      )}
    </div>
  );
}

export default function CoinField({
  onClick,
  symbol,
  value,
  onChange,
  activeField,
  balance,
  decimal,
  showMax = true,
  showZiroBalance = false,
}) {
  // This component is used to selecting a token and entering a value, the props are explained below:
  //      onClick - (string) => void - Called when the button is clicked
  //      symbol - string - The text displayed on the button
  //      value - string - The value of the text field
  //      onChange - (e) => void - Called when the text field changes
  //      activeField - boolean - Whether text can be entered into this field or not

  const classes = useStyles();
  const [iconImage] = useIcon();

  return (
    <div className={classes.container}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        className={classes.grid}
      >
        {/* Text Field */}
        <InputBase
          value={value}
          onChange={onChange}
          placeholder="0.0"
          disabled={!activeField}
          classes={{ root: classes.input, input: classes.inputBase }}
        />

        {/* Button */}

        <Fab
          size="medium"
          variant="extended"
          color={symbol ? "default" : "primary"}
          onClick={onClick}
          className={classes.fab}
        >
          {symbol ? (
            <>
              {typeof symbol === "string" ? (
                <>
                  <img
                    src={iconImage(symbol)}
                    alt={symbol}
                    className={classes.coinImage}
                  />
                  <span className={classes.coinSymbol}>{symbol}</span>
                </>
              ) : (
                <>
                  <img
                    src={iconImage(symbol[0])}
                    alt={symbol[0]}
                    className={classes.coinImage}
                  />
                  <img
                    src={iconImage(symbol[1])}
                    alt={symbol[1]}
                    className={classes.coinImageNegative}
                  />
                  <span className={classes.coinSymbol}>
                    {symbol[0]}-{symbol[1]}
                  </span>
                </>
              )}
            </>
          ) : (
            "Select"
          )}
          {activeField && <ExpandMoreIcon />}
        </Fab>
      </Grid>

      {(!!balance || showZiroBalance) && (
        <div className="d-flex align-items-center justify-content-end mt-1">
          <span>
            Balance:{" "}
            {balance
              ? Number(balance)
                  .toFixed(decimal)
                  .replace(/\.?0+$/, "")
              : 0}
          </span>

          {showMax && balance && (
            <Fab
              variant="extended"
              color="primary"
              className={classes.fab_max}
              onClick={() =>
                onChange({
                  target: {
                    value: Number(balance)
                      .toFixed(decimal)
                      .replace(/\.?0+$/, ""),
                  },
                })
              }
            >
              Max
            </Fab>
          )}
        </div>
      )}
    </div>
  );
}

export function CreatePool({
  onClick1,
  onClick2,
  symbol1,
  symbol2,
  value,
  onChange,
}) {
  // This component is used to selecting a coin and entering a value, the props are explained below:
  //      onClick - (string) => void - Called when the button is clicked
  //      symbol - string - The text displayed on the button
  //      value - string - The value of the text field
  //      onChange - (e) => void - Called when the text field changes
  //      activeField - boolean - Whether text can be entered into this field or not
  const [iconImage] = useIcon();

  const classes = useStyles();

  return (
    <div className={classes.container_blank}>
      <Grid
        container
        spacing={2}
        direction="column"
        justifyContent="space-between"
        alignItems="center"
        className={`flex-wrap ${classes.grid}`}
      >
        {/* Button */}
        <Grid item xs={6} sm={3}>
          <Fab
            size="medium"
            variant="extended"
            color={symbol1 ? "default" : "primary"}
            onClick={onClick1}
            className={classes.fab}
          >
            {symbol1 ? (
              <>
                {typeof symbol1 === "string" ? (
                  <>
                    <img
                      src={iconImage(symbol1)}
                      alt={symbol1}
                      className={classes.coinImage}
                    />
                    <span className={classes.coinSymbol}>{symbol1}</span>
                  </>
                ) : (
                  <>
                    <img
                      src={iconImage(symbol1[0])}
                      alt={symbol1[0]}
                      className={classes.coinImage}
                    />
                    <img
                      src={iconImage(symbol1[1])}
                      alt={symbol1[1]}
                      className={classes.coinImageNegative}
                    />
                    <span className={classes.coinSymbol}>
                      {symbol1[0]}-{symbol1[1]}
                    </span>
                  </>
                )}
              </>
            ) : (
              "Reward"
            )}
            <ExpandMoreIcon />
          </Fab>
        </Grid>

        {/* Button */}
        <Grid item xs={6} sm={3}>
          <Fab
            size="medium"
            variant="extended"
            color={symbol2 ? "default" : "primary"}
            onClick={onClick2}
            className={classes.fab}
          >
            {symbol2 ? (
              <>
                {typeof symbol2 === "string" ? (
                  <>
                    <img
                      src={iconImage(symbol2)}
                      alt={symbol2}
                      className={classes.coinImage}
                    />
                    <span className={classes.coinSymbol}>{symbol2}</span>
                  </>
                ) : (
                  <>
                    <img
                      src={iconImage(symbol2[0])}
                      alt={symbol2[0]}
                      className={classes.coinImage}
                    />
                    <img
                      src={iconImage(symbol2[1])}
                      alt={symbol2[1]}
                      className={classes.coinImageNegative}
                    />
                    <span className={classes.coinSymbol}>
                      {symbol2[0]}-{symbol2[1]}
                    </span>
                  </>
                )}
              </>
            ) : (
              "Stake"
            )}
            <ExpandMoreIcon />
          </Fab>
        </Grid>

        {/* Text Field */}
        <Grid item xs={12} sm={6} className="mt-2 mt-sm-0">
          <InputBase
            value={value}
            onChange={onChange}
            placeholder="Pool Duration (in seconds)"
            classes={{
              root: classes.container_input,
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
}
